import type {
  AuditEvent,
  CapabilityRecord,
  CertificateAuthorityConnector,
  CertificateSet,
  CryptoPolicy,
  DependencyBlastRadius,
  DependencyObservation,
  PolicyImpactAssessment,
  PlatformPolicy,
  IntegrationKind,
  IntegrationRecord,
  MigrationMaintenanceWindow,
  MigrationOrdering,
  MigrationPlan,
  MigrationSafeguards,
  TransactionRecord,
} from "@edgepki/protocol";
import { capabilityEvidenceDigest, dependencyEvidenceDigest, sha256 } from "@edgepki/protocol";
export type { IntegrationKind, IntegrationRecord } from "@edgepki/protocol";

export type ProductRole =
  | "tenant_admin"
  | "pki_operator"
  | "break_glass_operator"
  | "auditor"
  | "application_owner"
  | "read_only";

export interface NodeRecord {
  id: string;
  name: string;
  platform: string;
  version: string;
  status: "healthy" | "degraded" | "offline" | "disabled";
  capabilities: string[];
  public_key_sha256: string;
  last_seen_at: string;
}

export interface Overview {
  tenant_id: string;
  trust_status: "healthy" | "degraded" | "issuance_paused";
  issuance_paused: boolean;
  counts: Record<string, number>;
  pqc: {
    ready_percent: number;
    hybrid_percent: number;
    classical_only_percent: number;
    ready_endpoints?: number;
  };
  policy_version: number;
  approval_quorum: {
    required: number;
    eligible_nodes: number;
    healthy_nodes: number;
    available: boolean;
  };
  audit_head: { sequence: number; hash: string };
  audit_witness: {
    checkpoint_sequence: number;
    required: number;
    received: number;
    node_ids: string[];
    status: "healthy" | "pending" | "not_configured";
  };
}

export interface NodeEnrollmentPackage {
  id: string;
  enrollment_token: string;
  token_displayed_once: true;
  tenant_id: string;
  node_name: string;
  platform: "linux" | "windows" | "kubernetes";
  expires_at: string;
  config: Record<string, unknown>;
  policy_trust: Record<string, unknown>;
  registration_trust: Record<string, unknown>;
  bootstrap_package: {
    protocol: "sectigo-edge.node-bootstrap-envelope.v1";
    key_id: string;
    algorithm: "Ed25519";
    payload_base64: string;
    payload_sha256: string;
    signature: string;
  };
}

export interface PolicyProposal {
  version: number;
  digest: string;
  policy: CryptoPolicy;
  impact?: PolicyImpactAssessment;
  proposed_by: string;
  approved_by: string | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  updated_at: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  groups: string[];
  mfa: boolean;
  mfa_method?: "totp" | "passkey" | "federated";
  phishing_resistant_mfa?: boolean;
  phishing_resistant_mfa_required?: boolean;
  authentication_method?: "development" | "sso" | "password";
  tenant_id: string;
  tenant_name?: string;
}

export interface ScimConnectorStatus {
  configured: boolean;
  status: "active" | "revoked" | "not_configured";
  base_url: string;
  token_hint?: string;
  token_version?: number;
  created_at?: string;
  updated_at?: string;
  last_used_at?: string;
  active_users?: number;
}

export interface ScimConnectorCredential extends ScimConnectorStatus {
  bearer_token: string;
  token_displayed_once: true;
}

export interface AccessAssignment {
  subject: string;
  subject_type: "user" | "group";
  immutable_subject?: string;
  display_subject: string;
  active: boolean;
  role: ProductRole;
}

export interface AccessAssignmentPage {
  items: AccessAssignment[];
  next_cursor?: string;
}

export interface CRLPublicationStatus {
  connector_id: string;
  generation: number;
  previous_crl_sha256: string;
  crl_sha256: string;
  crl_number: string;
  this_update: string;
  next_update: string;
  issuer_spki_sha256: string;
  provider_reference: string;
  signer_receipt: string;
  r2_key: string;
  published_at: string;
  status: "healthy" | "pending" | "expired" | "external";
  required_revocations: number;
}

export interface OCSPPublicationStatus {
  connector_id: string;
  transaction_id: string;
  serial_number: string;
  hash_algorithm: "sha1" | "sha256";
  request_key: string;
  generation: number;
  previous_response_sha256: string;
  response_sha256: string;
  status: "good" | "revoked";
  produced_at: string;
  this_update: string;
  next_update: string;
  revoked_at?: string;
  revocation_reason?: string;
  responder_spki_sha256: string;
  provider_reference: string;
  signer_receipt: string;
  r2_key: string;
  published_at: string;
  health: "healthy" | "pending" | "refresh_due" | "external";
}

export interface OCSPPublicationPage {
  items: OCSPPublicationStatus[];
  next_cursor?: string;
}

export interface ServiceRegistration {
  node_id: string;
  claim_id: string;
  status: "pending_admin" | "approved" | "active";
  issuer: string;
  registration_message_id: string;
  announcement: {
    runtime_id: string;
    display_name: string;
    requested_spiffe_id: string;
    protocols: string[];
    capabilities: string[];
    endpoints: string[];
    measurements: Array<{ kind: string; value: string }>;
    site_hint?: string;
    owner_hint?: string;
  };
  approval?: {
    spiffe_id: string;
    owner: string;
    profile_id: string;
    channels: string[];
    selectors: Array<{ type: string; value: string }>;
    approved_by: string;
    approved_at: string;
  };
  spire_entry_id?: string;
  created_at: string;
  expires_at: string;
  updated_at: string;
}

export interface ConsoleData {
  overview: Overview;
  user: CurrentUser;
  nodes: NodeRecord[];
  capabilities: CapabilityRecord[];
  dependencies: DependencyObservation[];
  registrations: ServiceRegistration[];
  transactions: TransactionRecord[];
  certificateSets: CertificateSet[];
  cas: CertificateAuthorityConnector[];
  crlPublications: CRLPublicationStatus[];
  ocspPublications: OCSPPublicationStatus[];
  ocspNextCursor?: string;
  policy: CryptoPolicy;
  platformPolicy: PlatformPolicy;
  platformPolicySHA256: string;
  audit: AuditEvent[];
  scimConnector?: ScimConnectorStatus;
  integrations: IntegrationRecord[];
  migrations: MigrationPlan[];
  breakGlass?: BreakGlassState;
  demo: boolean;
}

export interface BreakGlassResumeRequest {
  id: string;
  action: "resume_issuance";
  status: "pending" | "approved" | "expired" | "cancelled";
  pause_generation: number;
  reason: string;
  requested_by: string;
  requested_at: string;
  expires_at: string;
  approved_by?: string;
  approved_at?: string;
  cancelled_at?: string;
}

export interface BreakGlassState {
  paused: boolean;
  pause_generation: number;
  requests: BreakGlassResumeRequest[];
}

const requestedTenant = typeof location === "undefined" ? null : new URLSearchParams(location.search).get("tenant");
const tenant = requestedTenant && /^[a-zA-Z0-9][a-zA-Z0-9._-]{2,127}$/.test(requestedTenant)
  ? requestedTenant
  : "acme-corp";

export class ConsoleApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ConsoleApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("accept", "application/json");
  headers.set("x-edgepki-tenant", tenant);
  if (init?.body) headers.set("content-type", "application/json");
  const response = await fetch(path, { ...init, headers });
  if (
    !response.headers
      .get("content-type")
      ?.toLowerCase()
      .includes("application/json")
  ) {
    throw new Error("Sectigo Edge API returned an unexpected response");
  }
  const body = (await response.json().catch(() => ({}))) as T & {
    error?: { message?: string };
  };
  if (!response.ok)
    throw new ConsoleApiError(
      response.status,
      (body.error as { code?: string } | undefined)?.code ?? "request_failed",
      body.error?.message ?? `Request failed (${response.status})`,
    );
  return body;
}

const demoPolicy: CryptoPolicy = {
  id: "enterprise-standard-2027",
  tenant_id: tenant,
  version: 12,
  status: "active",
  signatures: {
    preferred: ["ML-DSA-65", "ECDSA-P384-SHA384"],
    allowed: ["ECDSA-P256-SHA256", "ECDSA-P384-SHA384", "ML-DSA-65"],
    forbidden: ["RSA-PSS-3072-SHA384"],
  },
  key_establishment: {
    preferred: ["X25519MLKEM768"],
    allowed: ["X25519", "X25519MLKEM768"],
  },
  validity: {
    default_seconds: 86_400,
    max_seconds: 604_800,
    offline_renewal_max_seconds: 0,
  },
  workload_profiles: {
    "prod-service": {
      id: "prod-service",
      allowed_spiffe_prefixes: ["spiffe://acme/prod/"],
      allowed_dns_suffixes: ["prod.internal.example", "example.com"],
      allowed_algorithms: ["ECDSA-P256-SHA256", "ECDSA-P384-SHA384", "ML-DSA-65"],
      max_validity_seconds: 86_400,
      allowed_deployment_modes: ["single", "dual_slot", "nginx", "apache", "java_keystore", "hashicorp_vault"],
      require_capability_evidence: true,
      rotation: {
        mode: "dual_slot",
        renew_before_seconds: 21_600,
        overlap_seconds: 3_600,
        health_check_grace_seconds: 300,
        promotion: "automatic_after_health",
        rollback_window_seconds: 3_600,
      },
    },
    "agent-mtls": {
      id: "agent-mtls",
      allowed_spiffe_prefixes: ["spiffe://acme/agents/"],
      allowed_dns_suffixes: ["agents.internal.example", "example.com"],
      allowed_algorithms: ["ECDSA-P256-SHA256", "ECDSA-P384-SHA384"],
      max_validity_seconds: 3_600,
      allowed_deployment_modes: ["single", "dual_slot"],
      require_capability_evidence: false,
    },
    "kubernetes-workload": {
      id: "kubernetes-workload",
      allowed_spiffe_prefixes: ["spiffe://acme/kubernetes/"],
      allowed_dns_suffixes: ["cluster.local", "example.com"],
      allowed_algorithms: ["ECDSA-P256-SHA256", "ECDSA-P384-SHA384"],
      max_validity_seconds: 21_600,
      allowed_deployment_modes: ["single", "dual_slot"],
      require_capability_evidence: false,
    },
  },
  customer_approval_quorum: { required: 2 },
  issuance_guardrails: {
    mode: "enforce",
    max_requests_per_requester_5m: 20,
    max_unique_sans_per_requester_24h: 50,
    block_algorithm_downgrade: true,
    max_validity_increase_percent: 25,
    history_lookback_days: 30,
  },
  transition: {
    mode: "hybrid",
    pqc_required_after: "2028-01-01T00:00:00.000Z",
  },
  not_before: "2026-01-01T00:00:00.000Z",
  expires_at: "2027-12-31T23:59:59.000Z",
};

const demoPlatformPolicy: PlatformPolicy = {
  id: "sectigo-private-pki-baseline-2026",
  version: 2,
  status: "active",
  signatures: {
    allowed: ["ECDSA-P256-SHA256", "ECDSA-P384-SHA384", "RSA-PSS-3072-SHA384", "ML-DSA-65", "SLH-DSA-SHA2-128s", "HYBRID-ECDSA-P384-ML-DSA-65"],
    forbidden: [],
  },
  key_establishment: { allowed: ["X25519", "X25519MLKEM768"] },
  validity: { max_seconds: 2_592_000, offline_renewal_max_seconds: 86_400 },
  allowed_deployment_modes: ["single", "dual_slot", "microsoft_adcs", "nginx", "apache", "java_keystore", "hashicorp_vault"],
  minimum_customer_approvals: 1,
  require_capability_evidence: false,
  issuance_guardrails: {
    mode: "enforce", max_requests_per_requester_5m: 30,
    max_unique_sans_per_requester_24h: 100, block_algorithm_downgrade: true,
    max_validity_increase_percent: 50, history_lookback_days: 30,
  },
  not_before: "2026-08-29T00:00:00.000Z",
  expires_at: "2028-09-30T00:00:00.000Z",
};

async function demoData(): Promise<ConsoleData> {
  const now = new Date();
  const activeAfter = new Date(now.getTime() + 22 * 60 * 60_000).toISOString();
  const oldAfter = new Date(
    now.getTime() + 19 * 24 * 60 * 60_000,
  ).toISOString();
  const set: CertificateSet = {
    id: "certset_payments_prod",
    tenant_id: tenant,
    endpoint_id: "spiffe://acme/prod/payments",
    profile_id: "prod-service",
    generation: 42,
    rotation_policy: {
      mode: "dual_slot",
      renew_before_seconds: 21_600,
      overlap_seconds: 3600,
      health_check_grace_seconds: 300,
      promotion: "automatic_after_health",
      rollback_window_seconds: 3600,
    },
    active: {
      slot: "active",
      certificate_id: "cert_01JACTIVE",
      serial_number: "7f3a91c2e8",
      not_before: new Date(now.getTime() - 5 * 24 * 60 * 60_000).toISOString(),
      not_after: oldAfter,
      state: "serving",
      health: "healthy",
      deployed_at: new Date(now.getTime() - 5 * 24 * 60 * 60_000).toISOString(),
    },
    standby: {
      slot: "standby",
      certificate_id: "cert_01JSTANDBY",
      serial_number: "2de84a0b11",
      not_before: now.toISOString(),
      not_after: activeAfter,
      state: "staged",
      health: "healthy",
      deployed_at: new Date(now.getTime() - 4 * 60_000).toISOString(),
    },
  };
  const demoCapabilityEvidence: Omit<CapabilityRecord, "evidence_sha256"> = {
    id: "cap-demo-payments-01",
    tenant_id: tenant,
    endpoint_id: "spiffe://acme/prod/payments",
    observed_at: new Date(now.getTime() - 18 * 60_000).toISOString(),
    expires_at: new Date(now.getTime() + 42 * 60_000).toISOString(),
    source: "sdk_self_report",
    tls_versions: ["TLS1.3"],
    signature_algorithms: ["ECDSA-P384-SHA384", "ML-DSA-65"],
    key_establishment: ["X25519", "X25519MLKEM768"],
    application: { name: "payments-api", version: "4.18.2", os: "linux" },
  };
  const dependencyTime = new Date(now.getTime() - 2 * 60_000).toISOString();
  const dependencyExpiry = new Date(now.getTime() + 48 * 60_000).toISOString();
  const dependencyInputs: Array<Pick<DependencyObservation, "id" | "source_endpoint_id" | "target_endpoint_id" | "target_dns_name" | "target_port" | "channel" | "sample_count">> = [
    { id: "dep_demo_gateway_frontend", source_endpoint_id: "spiffe://acme/prod/mobile-gateway", target_endpoint_id: "spiffe://acme/prod/frontend", target_dns_name: "frontend.prod.internal.example", target_port: 443, channel: "grpc", sample_count: 28_481 },
    { id: "dep_demo_frontend_payments", source_endpoint_id: "spiffe://acme/prod/frontend", target_endpoint_id: "spiffe://acme/prod/payments", target_dns_name: "payments.prod.internal.example", target_port: 443, channel: "grpc", sample_count: 91_208 },
    { id: "dep_demo_checkout_payments", source_endpoint_id: "spiffe://acme/agents/checkout", target_endpoint_id: "spiffe://acme/prod/payments", target_dns_name: "payments.prod.internal.example", target_port: 443, channel: "a2a", sample_count: 8_714 },
    { id: "dep_demo_risk_payments", source_endpoint_id: "spiffe://acme.example/agent/prod/risk-reviewer", target_endpoint_id: "spiffe://acme/prod/payments", target_dns_name: "payments.prod.internal.example", target_port: 443, channel: "mcp", sample_count: 2_910 },
    { id: "dep_demo_payments_ledger", source_endpoint_id: "spiffe://acme/prod/payments", target_endpoint_id: "spiffe://acme/prod/ledger", target_dns_name: "ledger.prod.internal.example", target_port: 5432, channel: "database", sample_count: 114_002 },
    { id: "dep_demo_inventory_ledger", source_endpoint_id: "spiffe://acme.example/mcp/prod/inventory", target_endpoint_id: "spiffe://acme/prod/ledger", target_dns_name: "ledger.prod.internal.example", target_port: 443, channel: "mcp", sample_count: 5_122 },
    { id: "dep_demo_ledger_kms", source_endpoint_id: "spiffe://acme/prod/ledger", target_endpoint_id: "spiffe://acme/prod/key-service", target_dns_name: "keys.prod.internal.example", target_port: 8443, channel: "mtls", sample_count: 114_002 },
  ];
  const dependencies = await Promise.all(dependencyInputs.map(async (input) => {
    const material: Omit<DependencyObservation, "evidence_sha256"> = {
      protocol: "sectigo-edge.dependency-observation.v1", tenant_id: tenant, observer_node_id: "node-toronto-01",
      evidence_source: "sdk_runtime", first_observed_at: dependencyTime, last_observed_at: dependencyTime,
      expires_at: dependencyExpiry, ...input,
    };
    return { ...material, evidence_sha256: await dependencyEvidenceDigest(material) };
  }));
  const demoCustomerDecision: TransactionRecord["customer_decision"] = {
    decision: "allow", policy_id: demoPolicy.id, policy_version: demoPolicy.version, policy_sha256: await sha256(demoPolicy),
    evaluated_at: new Date(now.getTime() - 75_000).toISOString(), reasons: [], effective_algorithm: "ECDSA-P384-SHA384", effective_validity_seconds: 3_600, effective_profile_id: "agent-mtls",
  };
  const demoRiskTransaction: TransactionRecord["transaction"] = {
    protocol: "sharppki.v1", transaction_id: "tx_01KDEMOISSUANCERISK000000", tenant_id: tenant,
    requester: "spiffe://acme/agents/checkout", node_id: "node-toronto-01",
    request: { csr_der_base64: "AA==", sans: ["checkout.agents.internal.example", "checkout-shadow.agents.internal.example"], requested_algorithm: "ECDSA-P384-SHA384", validity_seconds: 3_600, profile_id: "agent-mtls", deployment_mode: "dual_slot" },
    nonce: "nonce_demo_issuance_risk_000001", issued_at: new Date(now.getTime() - 80_000).toISOString(), expires_at: new Date(now.getTime() + 220_000).toISOString(),
    policy_version: demoPolicy.version, customer_decision_sha256: await sha256(demoCustomerDecision),
  };
  const demoRiskMaterial: Omit<NonNullable<TransactionRecord["issuance_risk"]>, "evidence_sha256"> = {
    protocol: "sectigo-edge.issuance-risk.v1", transaction_id: demoRiskTransaction.transaction_id, tenant_id: tenant,
    requester: demoRiskTransaction.requester, profile_id: demoRiskTransaction.request.profile_id,
    policy_id: demoPolicy.id, policy_version: demoPolicy.version, policy_sha256: demoCustomerDecision.policy_sha256,
    platform_policy_id: demoPlatformPolicy.id, platform_policy_version: demoPlatformPolicy.version, platform_policy_sha256: await sha256(demoPlatformPolicy),
    effective_guardrails: demoPolicy.issuance_guardrails!, decision: "block",
    signals: [{ type: "san_expansion", observed: 74, limit: demoPolicy.issuance_guardrails!.max_unique_sans_per_requester_24h, explanation: "Requester SAN namespace expansion exceeds the signed 24-hour limit" }],
    history_sample_size: 43, history_commitment_sha256: await sha256(["demo bounded issuance history"]), evaluated_at: new Date(now.getTime() - 74_000).toISOString(),
  };
  const demoIssuanceRisk = { ...demoRiskMaterial, evidence_sha256: await sha256(demoRiskMaterial) };
  const demoSectigoDecision: TransactionRecord["sectigo_decision"] = {
    ...demoCustomerDecision, evaluated_at: new Date(now.getTime() - 73_000).toISOString(),
    platform_policy_id: demoPlatformPolicy.id, platform_policy_version: demoPlatformPolicy.version, platform_policy_sha256: demoRiskMaterial.platform_policy_sha256,
    risk_assessment_sha256: demoIssuanceRisk.evidence_sha256,
  };
  const demoRiskRecord: TransactionRecord = {
    transaction: demoRiskTransaction, digest: await sha256(demoRiskTransaction), status: "denied",
    customer_approval: { protocol: "sharppki.v1", key_id: "node-toronto-01", algorithm: "Ed25519", payload: demoRiskTransaction, payload_sha256: await sha256(demoRiskTransaction), signature: "demo-signed-customer-approval" },
    customer_decision: demoCustomerDecision, sectigo_decision: demoSectigoDecision, issuance_risk: demoIssuanceRisk, platform_policy: demoPlatformPolicy,
    created_at: demoRiskTransaction.issued_at, updated_at: new Date(now.getTime() - 72_000).toISOString(),
  };
  return {
    demo: true,
    user: {
      id: "user:demo",
      email: "alex.morgan@acme.example",
      groups: ["PKI Administrators"],
      mfa: true,
      tenant_id: tenant,
    },
    overview: {
      tenant_id: tenant,
      trust_status: "healthy",
      issuance_paused: false,
      counts: {
        nodes: 3,
        certificates: 13_812,
        issued: 13_812,
        expiring_30d: 18,
        policy_denials: 7,
        capability_endpoints: 13_812,
      },
      pqc: {
        ready_percent: 61,
        hybrid_percent: 83,
        classical_only_percent: 17,
        ready_endpoints: 8_425,
      },
      policy_version: 12,
      approval_quorum: {
        required: 2,
        eligible_nodes: 3,
        healthy_nodes: 3,
        available: true,
      },
      audit_head: { sequence: 845_219, hash: "q1Z4nQm8J9sc7dP52jxA4sE0vBf7" },
      audit_witness: {
        checkpoint_sequence: 845_219,
        required: 2,
        received: 2,
        node_ids: ["node-toronto-01", "node-virginia-01"],
        status: "healthy",
      },
    },
    breakGlass: { paused: false, pause_generation: 0, requests: [] },
    nodes: [
      {
        id: "node-toronto-01",
        name: "Toronto production",
        platform: "kubernetes",
        version: "0.1.9",
        status: "healthy",
        capabilities: ["acme", "dual-slot", "pqc-discovery"],
        public_key_sha256: "9d02…e81b",
        last_seen_at: now.toISOString(),
      },
      {
        id: "node-virginia-01",
        name: "Virginia production",
        platform: "linux",
        version: "0.1.9",
        status: "healthy",
        capabilities: [
          "acme",
          "dual-slot",
          "nginx",
          "nginx-atomic-rotation",
          "apache",
          "apache-atomic-rotation",
        ],
        public_key_sha256: "82a1…3f0c",
        last_seen_at: new Date(now.getTime() - 12_000).toISOString(),
      },
      {
        id: "node-frankfurt-01",
        name: "Frankfurt DR",
        platform: "windows",
        version: "0.1.9",
        status: "healthy",
        capabilities: ["acme", "dual-slot", "microsoft-adcs"],
        public_key_sha256: "11c9…72ad",
        last_seen_at: new Date(now.getTime() - 29_000).toISOString(),
      },
    ],
    capabilities: [
      {
        ...demoCapabilityEvidence,
        evidence_sha256: await capabilityEvidenceDigest(demoCapabilityEvidence),
      },
    ],
    dependencies,
    registrations: [
      {
        node_id: "node-toronto-01",
        claim_id: "claim_risk_reviewer",
        status: "pending_admin",
        issuer: "spiffe://acme.example/bootstrap/risk-reviewer-42",
        registration_message_id: "msg_risk_reviewer",
        announcement: {
          runtime_id: "risk-reviewer-42",
          display_name: "Risk review agent",
          requested_spiffe_id: "spiffe://acme.example/agent/prod/risk-reviewer",
          protocols: ["a2a", "mcp"],
          capabilities: ["risk.review", "policy.explain"],
          endpoints: ["https://risk-reviewer.internal:9444"],
          measurements: [
            {
              kind: "kubernetes-service-account",
              value: "agents/risk-reviewer",
            },
            {
              kind: "container-image-digest",
              value: `sha256:${"a".repeat(64)}`,
            },
          ],
          site_hint: "toronto-01",
          owner_hint: "AI Platform",
        },
        created_at: new Date(now.getTime() - 30_000).toISOString(),
        expires_at: new Date(now.getTime() + 9 * 60_000).toISOString(),
        updated_at: new Date(now.getTime() - 28_000).toISOString(),
      },
      {
        node_id: "node-toronto-01",
        claim_id: "claim_internal_mcp",
        status: "active",
        issuer: "spiffe://acme.example/bootstrap/inventory-mcp",
        registration_message_id: "msg_internal_mcp",
        announcement: {
          runtime_id: "inventory-mcp",
          display_name: "Inventory MCP server",
          requested_spiffe_id: "spiffe://acme.example/mcp/prod/inventory",
          protocols: ["mcp"],
          capabilities: ["inventory.read"],
          endpoints: ["https://inventory-mcp.internal/mcp"],
          measurements: [
            { kind: "systemd-unit", value: "inventory-mcp.service" },
          ],
          site_hint: "toronto-01",
          owner_hint: "Infrastructure",
        },
        approval: {
          spiffe_id: "spiffe://acme.example/mcp/prod/inventory",
          owner: "infrastructure@acme.example",
          profile_id: "mcp-server",
          channels: ["mcp"],
          selectors: [{ type: "systemd", value: "id:inventory-mcp.service" }],
          approved_by: "user:alex.morgan@acme.example",
          approved_at: new Date(now.getTime() - 14 * 60_000).toISOString(),
        },
        spire_entry_id: "d507a11e-7268-4ba8-a063-893752851a06",
        created_at: new Date(now.getTime() - 15 * 60_000).toISOString(),
        expires_at: new Date(now.getTime() - 5 * 60_000).toISOString(),
        updated_at: new Date(now.getTime() - 13 * 60_000).toISOString(),
      },
    ],
    transactions: [demoRiskRecord],
    certificateSets: [set],
    cas: [
      {
        id: "ca-scm-prod",
        tenant_id: tenant,
        name: "Sectigo SCM — Production",
        provider: "sectigo_scm",
        status: "active",
        certificate_chain_pem: "",
        signer_key_reference: "scm:key:prod",
        scm_account_id: "ACME-48219",
        scm_ca_id: "Private-PKI-01",
        scm_organization_id: 18234,
        scm_certificate_type_id: 2675,
        created_at: "2026-07-01T12:00:00.000Z",
        updated_at: now.toISOString(),
      },
      {
        id: "ca-byo-lab",
        tenant_id: tenant,
        name: "ACME Lab Intermediate",
        provider: "byo_ca",
        status: "active",
        certificate_chain_pem: "",
        signer_key_reference: "hsm:key:lab",
        created_at: "2026-08-12T12:00:00.000Z",
        updated_at: now.toISOString(),
      },
      {
        id: "ca-adcs-prod",
        tenant_id: tenant,
        name: "Corporate ADCS Issuing CA",
        provider: "byo_ca",
        status: "active",
        certificate_chain_pem: "",
        signer_key_reference: "local/adcs-production",
        created_at: "2026-08-01T12:00:00.000Z",
        updated_at: now.toISOString(),
      },
    ],
    crlPublications: [
      {
        connector_id: "ca-scm-prod", generation: 47, previous_crl_sha256: "dGVzdC1wcmV2aW91cy1jcmwtZGlnZXN0LTAwMDAwMDAwMA",
        crl_sha256: "dGVzdC1jdXJyZW50LWNybC1kaWdlc3QtMDAwMDAwMDAwMA", crl_number: "47",
        this_update: new Date(now.getTime() - 4 * 60_000).toISOString(), next_update: new Date(now.getTime() + 20 * 60 * 60_000).toISOString(),
        issuer_spki_sha256: "dGVzdC1pc3N1ZXItc3BraS1kaWdlc3QtMDAwMDAwMDAwMA", provider_reference: "scm-crl:prod:47",
        signer_receipt: "sharppki-receipt-v1.demo", r2_key: "acme-corp/revocation/crls/ca-scm-prod/47.crl",
        published_at: new Date(now.getTime() - 4 * 60_000).toISOString(), status: "healthy", required_revocations: 12,
      },
      {
        connector_id: "ca-byo-lab", generation: 8, previous_crl_sha256: "dGVzdC1wcmV2aW91cy1sYWItZGlnZXN0LTAwMDAwMDAwMA",
        crl_sha256: "dGVzdC1jdXJyZW50LWxhYi1kaWdlc3QtMDAwMDAwMDAwMA", crl_number: "8",
        this_update: new Date(now.getTime() - 35 * 60_000).toISOString(), next_update: new Date(now.getTime() + 8 * 60 * 60_000).toISOString(),
        issuer_spki_sha256: "dGVzdC1sYWItaXNzdWVyLXNwa2ktZGlnZXN0LTAwMDAwMA", provider_reference: "hsm-crl:lab:8",
        signer_receipt: "sharppki-receipt-v1.demo", r2_key: "acme-corp/revocation/crls/ca-byo-lab/8.crl",
        published_at: new Date(now.getTime() - 35 * 60_000).toISOString(), status: "healthy", required_revocations: 3,
      },
      {
        connector_id: "ca-adcs-prod", generation: 126, previous_crl_sha256: "dGVzdC1hZGNzLXByZXZpb3VzLWNybC1kaWdlc3QtMDAwMDA",
        crl_sha256: "dGVzdC1hZGNzLWN1cnJlbnQtY3JsLWRpZ2VzdC0wMDAwMDA", crl_number: "126",
        this_update: new Date(now.getTime() - 2 * 60_000).toISOString(), next_update: new Date(now.getTime() + 7 * 24 * 60 * 60_000 - 2 * 60_000).toISOString(),
        issuer_spki_sha256: "dGVzdC1hZGNzLWlzc3Vlci1zcGtpLWRpZ2VzdC0wMDAwMDA", provider_reference: "adcs:local/adcs-production:126",
        signer_receipt: "sharppki-node-crl-v1:node-frankfurt-01:demo", r2_key: "acme-corp/revocation/crls/ca-adcs-prod/126.crl",
        published_at: new Date(now.getTime() - 2 * 60_000).toISOString(), status: "healthy", required_revocations: 4,
      },
    ],
    ocspPublications: [
      {
        connector_id: "ca-scm-prod", transaction_id: "tx_01JACTIVE", serial_number: "7f3a91c2e8", hash_algorithm: "sha1",
        request_key: "demo-sha1-active", generation: 47, previous_response_sha256: "demo-previous", response_sha256: "demo-response-sha1",
        status: "good", produced_at: new Date(now.getTime() - 4 * 60_000).toISOString(), this_update: new Date(now.getTime() - 5 * 60_000).toISOString(),
        next_update: new Date(now.getTime() + 20 * 60 * 60_000).toISOString(), responder_spki_sha256: "demo-responder-spki",
        provider_reference: "scm-ocsp:prod:47", signer_receipt: "sharppki-receipt-v1.demo", r2_key: "demo/ocsp/sha1",
        published_at: new Date(now.getTime() - 4 * 60_000).toISOString(), health: "healthy",
      },
      {
        connector_id: "ca-scm-prod", transaction_id: "tx_01JACTIVE", serial_number: "7f3a91c2e8", hash_algorithm: "sha256",
        request_key: "demo-sha256-active", generation: 47, previous_response_sha256: "demo-previous", response_sha256: "demo-response-sha256",
        status: "good", produced_at: new Date(now.getTime() - 4 * 60_000).toISOString(), this_update: new Date(now.getTime() - 5 * 60_000).toISOString(),
        next_update: new Date(now.getTime() + 20 * 60 * 60_000).toISOString(), responder_spki_sha256: "demo-responder-spki",
        provider_reference: "scm-ocsp:prod:47", signer_receipt: "sharppki-receipt-v1.demo", r2_key: "demo/ocsp/sha256",
        published_at: new Date(now.getTime() - 4 * 60_000).toISOString(), health: "healthy",
      },
      {
        connector_id: "ca-byo-lab", transaction_id: "tx_01JSTANDBY", serial_number: "2de84a0b11", hash_algorithm: "sha1",
        request_key: "demo-sha1-standby", generation: 8, previous_response_sha256: "demo-previous", response_sha256: "demo-response-standby-sha1",
        status: "good", produced_at: new Date(now.getTime() - 35 * 60_000).toISOString(), this_update: new Date(now.getTime() - 36 * 60_000).toISOString(),
        next_update: new Date(now.getTime() + 26 * 60_000).toISOString(), responder_spki_sha256: "demo-responder-spki-lab",
        provider_reference: "hsm-ocsp:lab:8", signer_receipt: "sharppki-receipt-v1.demo", r2_key: "demo/ocsp/lab-sha1",
        published_at: new Date(now.getTime() - 35 * 60_000).toISOString(), health: "refresh_due",
      },
      {
        connector_id: "ca-byo-lab", transaction_id: "tx_01JSTANDBY", serial_number: "2de84a0b11", hash_algorithm: "sha256",
        request_key: "demo-sha256-standby", generation: 8, previous_response_sha256: "demo-previous", response_sha256: "demo-response-standby",
        status: "good", produced_at: new Date(now.getTime() - 35 * 60_000).toISOString(), this_update: new Date(now.getTime() - 36 * 60_000).toISOString(),
        next_update: new Date(now.getTime() + 26 * 60_000).toISOString(), responder_spki_sha256: "demo-responder-spki-lab",
        provider_reference: "hsm-ocsp:lab:8", signer_receipt: "sharppki-receipt-v1.demo", r2_key: "demo/ocsp/lab-sha256",
        published_at: new Date(now.getTime() - 35 * 60_000).toISOString(), health: "refresh_due",
      },
      {
        connector_id: "ca-adcs-prod", transaction_id: "tx_01JADCS", serial_number: "55ac02", hash_algorithm: "sha1",
        request_key: "demo-adcs-sha1", generation: 126, previous_response_sha256: "demo-adcs-previous", response_sha256: "demo-adcs-response-sha1", status: "good",
        produced_at: new Date(now.getTime() - 2 * 60_000).toISOString(), this_update: new Date(now.getTime() - 3 * 60_000).toISOString(),
        next_update: new Date(now.getTime() + 12 * 60 * 60_000).toISOString(), responder_spki_sha256: "demo-adcs-responder-spki",
        provider_reference: "adcs:local/adcs-production:126", signer_receipt: "sharppki-node-ocsp-v1:node-frankfurt-01:demo", r2_key: "demo/ocsp/adcs-sha1",
        published_at: new Date(now.getTime() - 2 * 60_000).toISOString(), health: "healthy",
      },
      {
        connector_id: "ca-adcs-prod", transaction_id: "tx_01JADCS", serial_number: "55ac02", hash_algorithm: "sha256",
        request_key: "demo-adcs-sha256", generation: 126, previous_response_sha256: "demo-adcs-previous", response_sha256: "demo-adcs-response-sha256", status: "good",
        produced_at: new Date(now.getTime() - 2 * 60_000).toISOString(), this_update: new Date(now.getTime() - 3 * 60_000).toISOString(),
        next_update: new Date(now.getTime() + 12 * 60 * 60_000).toISOString(), responder_spki_sha256: "demo-adcs-responder-spki",
        provider_reference: "adcs:local/adcs-production:126", signer_receipt: "sharppki-node-ocsp-v1:node-frankfurt-01:demo", r2_key: "demo/ocsp/adcs-sha256",
        published_at: new Date(now.getTime() - 2 * 60_000).toISOString(), health: "healthy",
      },
    ],
    policy: demoPolicy,
    platformPolicy: demoPlatformPolicy,
    platformPolicySHA256: await sha256(demoPlatformPolicy),
    scimConnector: {
      configured: true,
      status: "active",
      base_url: "https://api.sharppki.com/scim/acme-corp/v2",
      token_hint: "...G7kP",
      token_version: 3,
      created_at: "2026-07-01T12:00:00.000Z",
      updated_at: now.toISOString(),
      last_used_at: new Date(now.getTime() - 2 * 60_000).toISOString(),
    },
    integrations: [
      {
        kind: "acme",
        tenant_id: tenant,
        display_name: "ACME",
        status: "healthy",
        enabled: true,
        node_ids: ["node-toronto-01", "node-virginia-01"],
        profile_id: "prod-service",
        adapter_reference: "builtin/acme",
        revision: 8,
        applied_revision: 8,
        requested_action: "reconcile",
        last_checked_at: new Date(now.getTime() - 34_000).toISOString(),
        updated_at: new Date(now.getTime() - 60 * 60_000).toISOString(),
        updated_by: "user:demo",
      },
      {
        kind: "spiffe",
        tenant_id: tenant,
        display_name: "SPIFFE identities",
        status: "healthy",
        enabled: true,
        node_ids: ["node-toronto-01"],
        profile_id: "workload-mtls",
        adapter_reference: "builtin/spire",
        revision: 5,
        applied_revision: 5,
        requested_action: "reconcile",
        last_checked_at: new Date(now.getTime() - 22_000).toISOString(),
        updated_at: new Date(now.getTime() - 2 * 60 * 60_000).toISOString(),
        updated_by: "user:demo",
      },
      {
        kind: "nginx",
        tenant_id: tenant,
        display_name: "NGINX",
        status: "healthy",
        enabled: true,
        node_ids: ["node-virginia-01"],
        profile_id: "prod-service",
        adapter_reference: "local/nginx-production",
        revision: 4,
        applied_revision: 4,
        requested_action: "reconcile",
        node_statuses: {
          "node-virginia-01": {
            revision: 4,
            health: "healthy",
            checked_at: new Date(now.getTime() - 15_000).toISOString(),
          },
        },
        last_checked_at: new Date(now.getTime() - 15_000).toISOString(),
        updated_at: new Date(now.getTime() - 45 * 60_000).toISOString(),
        updated_by: "user:demo",
      },
      {
        kind: "apache",
        tenant_id: tenant,
        display_name: "Apache HTTP Server",
        status: "healthy",
        enabled: true,
        node_ids: ["node-virginia-01"],
        profile_id: "prod-service",
        adapter_reference: "local/apache-production",
        revision: 3,
        applied_revision: 3,
        requested_action: "reconcile",
        node_statuses: {
          "node-virginia-01": {
            revision: 3,
            health: "healthy",
            checked_at: new Date(now.getTime() - 18_000).toISOString(),
          },
        },
        last_checked_at: new Date(now.getTime() - 18_000).toISOString(),
        updated_at: new Date(now.getTime() - 50 * 60_000).toISOString(),
        updated_by: "user:demo",
      },
      {
        kind: "java_keystore",
        tenant_id: tenant,
        display_name: "Java PKCS#12 keystore",
        status: "healthy",
        enabled: true,
        node_ids: ["node-virginia-01"],
        profile_id: "prod-service",
        adapter_reference: "local/java-production",
        revision: 2,
        applied_revision: 2,
        requested_action: "reconcile",
        node_statuses: {
          "node-virginia-01": {
            revision: 2,
            health: "healthy",
            checked_at: new Date(now.getTime() - 12_000).toISOString(),
          },
        },
        last_checked_at: new Date(now.getTime() - 12_000).toISOString(),
        updated_at: new Date(now.getTime() - 40 * 60_000).toISOString(),
        updated_by: "user:demo",
      },
      {
        kind: "hashicorp_vault",
        tenant_id: tenant,
        display_name: "HashiCorp Vault KV v2",
        status: "disabled",
        enabled: false,
        node_ids: [],
        profile_id: "prod-service",
        revision: 0,
        applied_revision: 0,
        requested_action: "reconcile",
        updated_at: new Date(0).toISOString(),
        updated_by: "system:default",
      },
      {
        kind: "kubernetes_csr",
        tenant_id: tenant,
        display_name: "Kubernetes CSR",
        status: "disabled",
        enabled: false,
        node_ids: [],
        profile_id: "kubernetes-workload",
        revision: 0,
        applied_revision: 0,
        requested_action: "reconcile",
        updated_at: new Date(0).toISOString(),
        updated_by: "system:default",
      },
      {
        kind: "microsoft_adcs",
        tenant_id: tenant,
        display_name: "Microsoft ADCS",
        status: "healthy",
        enabled: true,
        node_ids: ["node-frankfurt-01"],
        profile_id: "windows-machine",
        adapter_reference: "local/adcs-production",
        ca_connector_id: "ca-adcs-prod",
        revision: 6,
        applied_revision: 6,
        requested_action: "reconcile",
        node_statuses: {
          "node-frankfurt-01": {
            revision: 6,
            health: "healthy",
            checked_at: new Date(now.getTime() - 18_000).toISOString(),
          },
        },
        last_checked_at: new Date(now.getTime() - 18_000).toISOString(),
        updated_at: new Date(now.getTime() - 4 * 60 * 60_000).toISOString(),
        updated_by: "user:demo",
      },
      {
        kind: "est",
        tenant_id: tenant,
        display_name: "EST",
        status: "disabled",
        enabled: false,
        node_ids: [],
        profile_id: "device-enrollment",
        revision: 0,
        applied_revision: 0,
        requested_action: "reconcile",
        updated_at: new Date(0).toISOString(),
        updated_by: "system:default",
      },
      {
        kind: "sectigo_scm",
        tenant_id: tenant,
        display_name: "Sectigo SCM",
        status: "healthy",
        enabled: true,
        node_ids: [],
        profile_id: "protected-signing",
        ca_connector_id: "ca-scm-prod",
        revision: 3,
        applied_revision: 3,
        requested_action: "reconcile",
        last_checked_at: new Date(now.getTime() - 41_000).toISOString(),
        updated_at: new Date(now.getTime() - 3 * 60 * 60_000).toISOString(),
        updated_by: "user:demo",
      },
    ],
    migrations: [],
    audit: [
      {
        id: "evt_845219",
        tenant_id: tenant,
        sequence: 845_219,
        type: "certificate.staged",
        actor: "node:node-toronto-01",
        transaction_id: "tx_01JSTANDBY",
        occurred_at: new Date(now.getTime() - 4 * 60_000).toISOString(),
        data: { slot: "standby" },
        previous_hash: "z8d…02a",
        event_hash: "q1Z…f7",
      },
      {
        id: "evt_845218",
        tenant_id: tenant,
        sequence: 845_218,
        type: "certificate.issued",
        actor: "system:sectigo-signer",
        transaction_id: "tx_01JSTANDBY",
        occurred_at: new Date(now.getTime() - 5 * 60_000).toISOString(),
        data: { provider: "sectigo_scm" },
        previous_hash: "a1e…f91",
        event_hash: "z8d…02a",
      },
      {
        id: "evt_845217",
        tenant_id: tenant,
        sequence: 845_217,
        type: "issuance.sectigo_approved",
        actor: "system:sectigo-edge",
        transaction_id: "tx_01JSTANDBY",
        occurred_at: new Date(now.getTime() - 5.2 * 60_000).toISOString(),
        data: { policy_version: 12 },
        previous_hash: "b74…e20",
        event_hash: "a1e…f91",
      },
    ],
  };
}

export async function loadConsoleData(): Promise<ConsoleData> {
  const demoRequested =
    location.pathname.startsWith("/console/demo") ||
    new URLSearchParams(location.search).get("demo") === "1";
  if (
    demoRequested ||
    ["localhost", "127.0.0.1"].includes(location.hostname) ||
    location.hostname.endsWith(".workers.dev")
  ) {
    return demoData();
  }
  try {
    const [
      overview,
      user,
      nodes,
      capabilities,
      dependencies,
      registrations,
      transactions,
      certificateSets,
      cas,
      crlPublications,
      ocspPage,
      policy,
      platformPolicyResponse,
      audit,
      integrations,
      migrations,
      scimConnector,
      breakGlass,
    ] = await Promise.all([
      request<Overview>("/api/v1/overview"),
      request<CurrentUser>("/api/v1/me"),
      request<NodeRecord[]>("/api/v1/nodes"),
      request<CapabilityRecord[]>("/api/v1/capabilities"),
      request<DependencyObservation[]>("/api/v1/dependencies"),
      request<ServiceRegistration[]>("/api/v1/registrations"),
      request<TransactionRecord[]>("/api/v1/transactions"),
      request<CertificateSet[]>("/api/v1/certificate-sets"),
      request<CertificateAuthorityConnector[]>("/api/v1/cas"),
      request<CRLPublicationStatus[]>("/api/v1/revocation/crls").catch(() => []),
      request<OCSPPublicationPage>("/api/v1/revocation/ocsp?limit=100").catch((): OCSPPublicationPage => ({ items: [] })),
      request<CryptoPolicy>("/api/v1/policy"),
      request<{ policy: PlatformPolicy; sha256: string }>("/api/v1/policy/platform"),
      request<AuditEvent[]>("/api/v1/audit"),
      request<IntegrationRecord[]>("/api/v1/integrations"),
      request<MigrationPlan[]>("/api/v1/migrations"),
      request<ScimConnectorStatus>("/api/v1/access/scim-connector").catch(
        () => undefined,
      ),
      request<BreakGlassState>("/api/v1/break-glass/resume-requests").catch(
        () => undefined,
      ),
    ]);
    return {
      overview,
      user,
      nodes,
      capabilities,
      dependencies,
      registrations,
      transactions,
      certificateSets,
      cas,
      crlPublications,
      ocspPublications: ocspPage.items,
      ...(ocspPage.next_cursor ? { ocspNextCursor: ocspPage.next_cursor } : {}),
      policy,
      platformPolicy: platformPolicyResponse.policy,
      platformPolicySHA256: platformPolicyResponse.sha256,
      audit,
      integrations,
      migrations,
      ...(scimConnector ? { scimConnector } : {}),
      ...(breakGlass ? { breakGlass } : {}),
      demo: false,
    };
  } catch (error) {
    throw error;
  }
}

export interface CreateMigrationPlanRequest {
  name: string;
  ordering: MigrationOrdering;
  maintenance_window: MigrationMaintenanceWindow;
  safeguards: MigrationSafeguards;
  targets: Array<{
    certificate_set_id: string;
    failure_domain: string;
    health_url: string;
  }>;
  predecessor?: {
    migration_plan_id: string;
    recovery_sha256: string;
  };
}

export async function createMigrationPlan(
  input: CreateMigrationPlanRequest,
): Promise<MigrationPlan> {
  return request("/api/v1/migrations", {
    method: "POST",
    body: JSON.stringify({ ...input, confirmation: "CREATE MIGRATION" }),
  });
}

export async function approveMigrationPlan(
  id: string,
  planSHA256: string,
): Promise<MigrationPlan> {
  return request(`/api/v1/migrations/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    body: JSON.stringify({
      plan_sha256: planSHA256,
      confirmation: "APPROVE MIGRATION",
    }),
  });
}

export async function startMigrationPlan(
  id: string,
  planSHA256: string,
): Promise<MigrationPlan> {
  return request(`/api/v1/migrations/${encodeURIComponent(id)}/start`, {
    method: "POST",
    body: JSON.stringify({
      plan_sha256: planSHA256,
      confirmation: "START MIGRATION",
    }),
  });
}

export async function requestMigrationRecovery(
  id: string,
  planSHA256: string,
  remediation: string,
): Promise<MigrationPlan> {
  return request(`/api/v1/migrations/${encodeURIComponent(id)}/recovery`, {
    method: "POST",
    body: JSON.stringify({ plan_sha256: planSHA256, remediation, confirmation: "REQUEST MIGRATION RECOVERY" }),
  });
}

export async function approveMigrationRecovery(
  id: string,
  recoverySHA256: string,
): Promise<MigrationPlan> {
  return request(`/api/v1/migrations/${encodeURIComponent(id)}/recovery/approve`, {
    method: "POST",
    body: JSON.stringify({ recovery_sha256: recoverySHA256, confirmation: "APPROVE MIGRATION RECOVERY" }),
  });
}

export interface RegistrationApprovalInput {
  human_code: string;
  owner: string;
  profile_id: string;
  channels: string[];
  x509_svid_ttl: number;
  jwt_svid_ttl: number;
}

export async function approveServiceRegistration(
  nodeId: string,
  claimId: string,
  input: RegistrationApprovalInput,
): Promise<void> {
  await request(
    `/api/v1/registrations/${encodeURIComponent(nodeId)}/${encodeURIComponent(claimId)}/approve`,
    {
      method: "POST",
      body: JSON.stringify({ ...input, confirmation: "CLAIM SERVICE" }),
    },
  );
}

export async function createNodeEnrollmentPackage(
  nodeName: string,
  platform: NodeEnrollmentPackage["platform"],
): Promise<NodeEnrollmentPackage> {
  return request("/api/v1/nodes/enrollment-packages", {
    method: "POST",
    body: JSON.stringify({ node_name: nodeName, platform, ttl_minutes: 30 }),
  });
}

export async function listPolicyProposals(): Promise<PolicyProposal[]> {
  return request("/api/v1/policy/proposals");
}

export async function previewPolicyImpact(policy: CryptoPolicy): Promise<PolicyImpactAssessment> {
  return request("/api/v1/policy/impact", {
    method: "POST",
    body: JSON.stringify({ policy }),
  });
}

export async function proposePolicy(policy: CryptoPolicy, impactSHA256: string): Promise<PolicyProposal> {
  return request("/api/v1/policy", { method: "PUT", body: JSON.stringify({ policy, impact_sha256: impactSHA256 }) });
}

export async function approvePolicy(version: number): Promise<CryptoPolicy> {
  return request(`/api/v1/policy/proposals/${version}/approve`, { method: "POST" });
}

export async function analyzeDependencyTargets(targetEndpointIds: string[]): Promise<DependencyBlastRadius> {
  return request("/api/v1/dependencies/blast-radius", {
    method: "POST",
    body: JSON.stringify({ target_endpoint_ids: targetEndpointIds }),
  });
}

export async function setKillSwitch(reason: string): Promise<{ paused: true; pause_generation: number }> {
  return request("/api/v1/kill-switch", {
    method: "POST",
    body: JSON.stringify({ paused: true, reason, confirmation: "PAUSE ISSUANCE" }),
  });
}

export async function requestIssuanceResume(reason: string): Promise<BreakGlassResumeRequest> {
  return request("/api/v1/break-glass/resume-requests", {
    method: "POST",
    body: JSON.stringify({
      reason,
      confirmation: "REQUEST ISSUANCE RESUME",
      idempotency_key: `console-${crypto.randomUUID()}`,
    }),
  });
}

export async function approveIssuanceResume(id: string): Promise<BreakGlassResumeRequest> {
  return request(`/api/v1/break-glass/resume-requests/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    body: JSON.stringify({ confirmation: "APPROVE ISSUANCE RESUME" }),
  });
}

export async function assignRole(
  subject: string,
  role: ProductRole,
  operation: "grant" | "revoke" = "grant",
): Promise<void> {
  await request("/api/v1/access/assignments", {
    method: "POST",
    body: JSON.stringify({ subject, role, operation }),
  });
}

export async function listAccessAssignments(cursor?: string): Promise<AccessAssignmentPage> {
  return request(`/api/v1/access/assignments${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`);
}

export async function rotateScimConnector(): Promise<ScimConnectorCredential> {
  return request("/api/v1/access/scim-connector", {
    method: "POST",
    body: JSON.stringify({ confirmation: "ROTATE SCIM TOKEN" }),
  });
}

export async function revokeScimConnector(): Promise<ScimConnectorStatus> {
  return request("/api/v1/access/scim-connector", {
    method: "DELETE",
    body: JSON.stringify({ confirmation: "REVOKE SCIM TOKEN" }),
  });
}

export async function promoteCertificateSet(id: string, healthURL: string): Promise<void> {
  await request(`/api/v1/certificate-sets/${encodeURIComponent(id)}/promote`, {
    method: "POST",
    body: JSON.stringify({ health_url: healthURL }),
  });
}

export async function revokeCertificate(
  id: string,
  reason: string,
  comment: string,
  promoteHealthyStandby = true,
): Promise<TransactionRecord> {
  return request(`/api/v1/certificates/${encodeURIComponent(id)}/revoke`, {
    method: "POST",
    body: JSON.stringify({
      reason,
      comment,
      promote_healthy_standby: promoteHealthyStandby,
    }),
  });
}

export async function refreshCrl(connectorId: string): Promise<CRLPublicationStatus> {
  return request(`/api/v1/revocation/crls/${encodeURIComponent(connectorId)}/refresh`, {
    method: "POST",
    body: JSON.stringify({ confirmation: "REFRESH CRL" }),
  });
}

export async function loadOCSPPage(cursor?: string): Promise<OCSPPublicationPage> {
  const query = new URLSearchParams({ limit: "100" });
  if (cursor) query.set("cursor", cursor);
  return request(`/api/v1/revocation/ocsp?${query.toString()}`);
}

export async function refreshOcsp(transactionId: string): Promise<OCSPPublicationStatus[]> {
  return request(`/api/v1/revocation/ocsp/${encodeURIComponent(transactionId)}/refresh`, {
    method: "POST",
    body: JSON.stringify({ confirmation: "REFRESH OCSP" }),
  });
}

export async function createCaImportSession(
  provider: "sectigo_scm" | "byo_ca",
  name: string,
  scmIds?: { scm_organization_id: number; scm_certificate_type_id: number },
): Promise<{
  upload_url?: string;
  authorize_url?: string;
  expires_at?: string;
}> {
  return request("/api/v1/cas/import-session", {
    method: "POST",
    body: JSON.stringify({ provider, name, ...(provider === "sectigo_scm" && scmIds ? scmIds : {}) }),
  });
}

/** Sets an SCM connector's enrollment IDs by re-posting the stored connector (the edge upserts and re-validates it). */
export async function updateCaScmIds(
  connector: CertificateAuthorityConnector,
  scmIds: { scm_organization_id: number; scm_certificate_type_id: number },
): Promise<CertificateAuthorityConnector> {
  const { incomplete_fields: _computed, ...stored } = connector;
  return request("/api/v1/cas", {
    method: "POST",
    body: JSON.stringify({ ...stored, ...scmIds }),
  });
}

export interface IntegrationConfigurationInput {
  enabled: boolean;
  node_ids: string[];
  profile_id: string;
  adapter_reference?: string;
  ca_connector_id?: string;
}

export async function saveIntegration(
  kind: IntegrationKind,
  input: IntegrationConfigurationInput,
): Promise<IntegrationRecord> {
  return request(`/api/v1/integrations/${encodeURIComponent(kind)}`, {
    method: "PUT",
    body: JSON.stringify({ ...input, confirmation: "SAVE INTEGRATION" }),
  });
}

export async function testIntegration(
  kind: IntegrationKind,
): Promise<IntegrationRecord> {
  return request(`/api/v1/integrations/${encodeURIComponent(kind)}/test`, {
    method: "POST",
    body: JSON.stringify({ confirmation: "TEST INTEGRATION" }),
  });
}

export async function exportTransactionEvidence(
  transactionId: string,
): Promise<void> {
  const response = await fetch(
    `/api/v1/transactions/${encodeURIComponent(transactionId)}/evidence`,
    {
      method: "POST",
      headers: { accept: "application/json", "x-edgepki-tenant": tenant },
    },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: { message?: string };
    };
    throw new Error(
      body.error?.message ?? `Evidence export failed (${response.status})`,
    );
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename =
    /filename="([^"]+)"/.exec(disposition)?.[1] ??
    `sharppki-evidence-${transactionId}.json`;
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}
